import accessTokenStorage from 'lib/accessTokenStorage';
import { getDefaultLanguage } from 'lib/languages';

const backendUrl = process.env.REACT_APP_BACKEND_URL;

function status(response) {
  if (response.ok) {
    return response;
  } else {
    const contentType = response.headers.get('content-type');
    if (contentType && contentType.includes('application/json')) {
      return response.json().then(body => {
        return Promise.reject({ ...body, status: response.status });
      });
    } else {
      return Promise.reject({
        message: response.statusText,
        status: response.status
      });
    }
  }
}

function json(response) {
  if (response.status !== 204) {
    const contentType = response.headers.get('content-type');
    if (contentType && contentType.includes('application/json')) {
      return response.json();
    }
    if (contentType && contentType.includes('text/html')) {
      return response.text();
    }
    return response;
  }
}

class ServerApi {
  constructor(accept = 'application/json') {
    this.opts = bodyExist => ({
      headers: {
        ...(accessTokenStorage.isExist()
          ? { Authorization: `Token ${accessTokenStorage.get()}` }
          : {}),
        'Content-Language': getDefaultLanguage(),
        Accept: accept,
        ...(bodyExist ? { 'Content-type': 'application/json' } : {})
      }
    });
    this.bodyConverter = body => JSON.stringify(body);
  }

  buildUrl(path) {
    return `${backendUrl}${path}`;
  }

  /**
   * Make GET ajax request.
   * @param url - requested url
   * @param withHeaders
   * @returns {Promise.<TResult>}
   */
  get(url, withHeaders = false, absoluteUrl = false) {
    return fetch(absoluteUrl ? url : this.buildUrl(url), {
      method: 'get',
      ...this.opts(false)
    })
      .then(status)
      .then(response => {
        if (withHeaders) {
          return json(response).then(data => ({
            data,
            headers: response.headers
          }));
        } else {
          return json(response);
        }
      });
  }
  /**
   * Make POST ajax request.
   * @param url - requested url
   * @param body - object {key: value}
   * @returns {Promise.<TResult>}
   */
  post(url, body) {
    return fetch(this.buildUrl(url), {
      method: 'post',
      ...this.opts(body),
      body: this.bodyConverter(body)
    })
      .then(status)
      .then(json);
  }

  patch(url, body) {
    return fetch(this.buildUrl(url), {
      method: 'PATCH',
      ...this.opts(body),
      body: this.bodyConverter(body)
    })
      .then(status)
      .then(json);
  }

  /**
   * Make PUT ajax request.
   * @param url - requested url
   * @param body - object {key: value}
   * @returns {Promise.<TResult>}
   */
  put(url, body) {
    return fetch(this.buildUrl(url), {
      method: 'put',
      ...this.opts(body),
      body: this.bodyConverter(body)
    })
      .then(status)
      .then(json);
  }

  /**
   * Make DELETE ajax request.
   * @param url - requested url
   * @param body - object {key: value}
   * @returns {Promise.<TResult>}
   */
  delete(url, body) {
    return fetch(this.buildUrl(url), {
      method: 'delete',
      ...this.opts(body),
      body: this.bodyConverter(body)
    })
      .then(status)
      .then(json);
  }
}

export default ServerApi;
